// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useRef } from "react";

/**
 * Helper hook for setTimeout that ensures the callback that is triggered
 * is always the callback from the latest render. Using setTimeout from
 * a hook will otherwise result in old state from the first render being
 * referenced.
 * @param callback The callback to trigger
 * @param durationMs The duration of the timeout
 */
export function useTimeout(callback: () => void, durationMs?: number, token?: any) {
    const savedCallback = useRef(callback);

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the timeout.
    useEffect(() => {
        function fire() {
            savedCallback.current();
        }

        if (durationMs !== undefined) {
            const id = setTimeout(fire, durationMs);
            return () => clearTimeout(id);
        }
    }, [durationMs, token]);
}
